import { OptionType } from '@evinced-private/ui-common';

import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

import { extractLabelKey, parseLabel } from '../../helpers/helpers';
import { TestRowSharedType } from '../../helpers/types';
import {
	createCustomFieldAddChange,
	createCustomFieldRemoveChange
} from '../builders/PayloadBuilder';
import { Change } from '../types/PayloadTypes';

import { buildLabelKeyMap, getConfiguredLabelKey } from './LabelChangeHelpers';

const extractAdditionKeys = (
	additions: Array<{ labelType: OptionType; value: OptionType }>
): Set<string> => {
	return new Set(additions.map((addition) => addition.labelType.label.toLowerCase()));
};

const buildRemovalChanges = (rowLabels: string[], additionKeys: Set<string>): Change[] => {
	const changes: Change[] = [];

	for (const label of rowLabels) {
		const parsed = parseLabel(label);
		if (parsed && additionKeys.has(parsed.key.toLowerCase())) {
			changes.push(createCustomFieldRemoveChange(parsed.key, parsed.value));
		}
	}

	return changes;
};

const getRemainingLabelKeys = (rowLabels: string[], additionKeys: Set<string>): Set<string> => {
	return new Set(
		rowLabels
			.map((label) => extractLabelKey(label))
			.filter((key) => !additionKeys.has(key.toLowerCase()))
	);
};

const buildAdditionChanges = (
	rowAdditions: Array<{ labelType: OptionType; value: OptionType }>,
	remainingLabelKeys: Set<string>,
	configuredLabels?: ConfigurationLabel[]
): Change[] => {
	const changes: Change[] = [];
	const labelMap = buildLabelKeyMap(configuredLabels);

	for (const addition of rowAdditions) {
		const additionKey = addition.labelType.label.toLowerCase();
		// Only add if the key doesn't already exist (after removals)
		if (!remainingLabelKeys.has(additionKey)) {
			const configuredKey = getConfiguredLabelKey(additionKey, labelMap);
			changes.push(createCustomFieldAddChange(configuredKey, addition.value.label));
		}
	}

	return changes;
};

export const buildAllRowChanges = (
	row: TestRowSharedType,
	rowAdditions: Array<{ labelType: OptionType; value: OptionType }>,
	configuredLabels?: ConfigurationLabel[]
): Change[] => {
	if (rowAdditions.length === 0) {
		return [];
	}

	const rowLabels = row.labels ?? [];
	const additionKeys = extractAdditionKeys(rowAdditions);

	const removalChanges = buildRemovalChanges(rowLabels, additionKeys);
	const remainingLabelKeys = getRemainingLabelKeys(rowLabels, additionKeys);
	const additionChanges = buildAdditionChanges(rowAdditions, remainingLabelKeys, configuredLabels);

	return [...removalChanges, ...additionChanges];
};
